import './assets/styles/main.scss';
import 'react-loading-skeleton/dist/skeleton.css';

import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { Route, Routes } from 'react-router-dom';
import { injectStyle } from 'react-toastify/dist/inject-style';

import { getEntraAuthInstance } from './auth/entraAuthConfig';
import EntraLoginPage from './auth/EntraLoginPage';
import EntraRequiredAuth from './auth/EntraRequiredAuth';
import EntraUserAuthenticationState from './auth/EntraUserAuthenticationState';
import DeprecatedNamespaceRedirect from './components/DeprecatedNamespaceRedirect';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import LastLeadRedirect from './components/LastLeadRedirect';
import LeadGidRedirect from './components/LeadGidRedirect';
import PersonGidRedirect from './components/PersonGidRedirect';
import RouteLoadingOverlay, {
  RouteLoadingFallback,
  RouteLoadingOverlayProvider
} from './components/RouteLoadingOverlay';
import TwilioFlexUrlRouter from './components/TwilioFlexUrlRouter';
import ExtrasLayoutRoute from './pages/ExtrasLayoutRoute';
import ProductAnnouncementEditor from './pages/ProductAnnouncements/_components/ProductAnnouncementEditor';
import ProductAnnouncementPage from './pages/ProductAnnouncements/_components/ProductAnnouncementPage';
import ScoutingReportScriptPreview from './pages/ScoutingReportScripts/ScoutingReportScriptPreview';
import ScoutingSteps from './pages/ScoutingReportScripts/ScoutingSteps';
import authInfo from './services/authInfo';

const AppRoot = React.lazy(() => import('./pages/AppRoot'));
const CoveragesMappings = React.lazy(() => import('./pages/CoveragesMappings'));
const CurrentCustomerInfo = React.lazy(() => import('./pages/CurrentCustomerInfo'));
const CustomerView = React.lazy(() => import('./pages/CustomerView'));
const GuidedSellingExperience = React.lazy(() => import('./pages/GuidedSellingExperience'));
const Lead = React.lazy(() => import('./pages/Lead'));
const RecentLeadsDashboard = React.lazy(() => import('./pages/LeadsDashboard/RecentLeadsDashboard'));
const MetricsDashboards = React.lazy(() => import('./pages/MetricsDashboards'));
const NewCustomer = React.lazy(() => import('./pages/NewCustomer'));
const NewLead = React.lazy(() => import('./pages/NewLead'));
const PeopleDashboard = React.lazy(() => import('./pages/PeopleDashboard'));
const PeopleSearch = React.lazy(() => import('./pages/PeopleSearch'));
const PoliciesDashboard = React.lazy(() => import('./pages/PoliciesDashboard'));
const ProductAnnouncements = React.lazy(() => import('./pages/ProductAnnouncements'));

const SalesDashboardTable = React.lazy(() => import('./pages/SalesDashboardTable'));
const ScoutingReportScripts = React.lazy(() => import('./pages/ScoutingReportScripts'));
const TasksDashboard = React.lazy(() => import('./pages/TasksDashboard'));

injectStyle();

const AppWithEntra = () => {
  const [isAuthenticatedToBackend, setIsAuthenticatedToBackend] = React.useState(false);
  const [entraInstance, setEntra] = React.useState<IPublicClientApplication>();

  React.useEffect(() => {
    getEntraAuthInstance().then(instance => {
      setEntra(instance);
    });
  }, []);

  if (!entraInstance) {
    return null;
  }

  return (
    <MsalProvider instance={entraInstance}>
      <Sentry.ErrorBoundary
        fallback={props => <ErrorFallback {...props} resetAuth={() => setIsAuthenticatedToBackend(false)} />}
      >
        <RouteLoadingOverlayProvider>
          <React.Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/crm/*" element={<DeprecatedNamespaceRedirect />} />
              <Route path="/login" element={<EntraLoginPage />} />
              <Route
                element={
                  <EntraUserAuthenticationState
                    isAuthenticatedToBackend={isAuthenticatedToBackend}
                    setIsAuthenticatedToBackend={setIsAuthenticatedToBackend}
                  />
                }
              >
                <Route element={<EntraRequiredAuth isAuthenticatedToBackend={isAuthenticatedToBackend} />}>
                  <Route element={<ExtrasLayoutRoute />}>
                    <Route path="max_client_request" element={<TwilioFlexUrlRouter />} />
                    <Route path="leads/gid/:gid" element={<LeadGidRedirect />} />
                    <Route path="customers/:customerId/*" element={<PersonGidRedirect />} />
                    <Route path="people/:personGid/last_lead" element={<LastLeadRedirect />} />
                    <Route element={<AppRoot />}>
                      <Route path="current_customer_info" element={<CurrentCustomerInfo />} />
                      <Route
                        path="scouting_report/*" // TODO: rename path to guided_selling_experience
                        element={<GuidedSellingExperience />}
                      />
                      <Route path="people_search" element={<PeopleSearch />} />
                      <Route path="new_lead" element={<NewLead />} />
                      <Route path="new_customer" element={<NewCustomer />} />
                      <Route path="leads/recent" element={<RecentLeadsDashboard />} />
                      <Route path="leads/:id/*" element={<Lead />} />
                      <Route path="leads" element={<SalesDashboardTable />} />
                      <Route path="policies" element={<PoliciesDashboard />} />
                      <Route path="tasks" element={<TasksDashboard />} />
                      <Route path="people/:gid/*" element={<CustomerView />} />
                      <Route path="customers" element={<PeopleDashboard />} />
                      {authInfo.features.amp_ui_product_tabs && (
                        <>
                          <Route path="dashboards" element={<MetricsDashboards />} />
                          <Route path="coverages_mappings" element={<CoveragesMappings />} />
                        </>
                      )}
                      <Route path="/" element={<SalesDashboardTable />} />
                      <Route path="scouting_steps" element={<ScoutingSteps />} />
                      <Route path="scouting_steps/:stepId" element={<ScoutingSteps />} />
                      <Route path="scouting_report_scripts" element={<ScoutingReportScripts />} />
                      <Route path="scouting_report_scripts/:strategyId" element={<ScoutingReportScripts />} />
                      <Route
                        path="scouting_report_scripts/:configKey/preview"
                        element={<ScoutingReportScriptPreview />}
                      />
                      <Route path="product_announcements" element={<ProductAnnouncements />} />
                      <Route
                        path="product_announcements/:productAnnouncementId"
                        element={<ProductAnnouncementPage />}
                      />
                      <Route path="product_announcements_admin" element={<ProductAnnouncements adminView />} />
                      <Route
                        path="product_announcements_admin/:productAnnouncementId"
                        element={<ProductAnnouncementPage adminView />}
                      />
                      <Route
                        path="product_announcements_admin/:productAnnouncementId/edit"
                        element={<ProductAnnouncementEditor />}
                      />
                      <Route path="new_product_announcement" element={<ProductAnnouncementEditor />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Routes>
          </React.Suspense>
          <RouteLoadingOverlay />
        </RouteLoadingOverlayProvider>
      </Sentry.ErrorBoundary>
    </MsalProvider>
  );
};

export default AppWithEntra;
